"use client";

import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Play, Pause, Volume2 } from "lucide-react";
import {
  buildGcsUrl,
  PODCASTIFY_DEMO_BUCKET,
} from "@/lib/storage/gcs";

export const DEFAULT_DEMO_AUDIO_URL = buildGcsUrl(
  PODCASTIFY_DEMO_BUCKET,
  "podcast_d7342521a57c4b0abf2618abcc5cd839.mp3",
);

interface AudioPlayerProps {
  src?: string;
  title?: string;
  durationLabel?: string;
  onPlay?: () => void;
}

export const DEFAULT_DEMO_AUDIO_TITLE = "Podcastify founder interview";

export function AudioPlayer({
  src = DEFAULT_DEMO_AUDIO_URL,
  title = DEFAULT_DEMO_AUDIO_TITLE,
  durationLabel = "1:03",
  onPlay,
}: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        void audioRef.current.play().catch(() => setIsPlaying(false));
        setIsPlaying(true);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const currentProgress =
        (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(currentProgress || 0);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickPosition = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = clickPosition * audioRef.current.duration;
    }
  };

  return (
    <>
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
          <Volume2 className="w-8 h-8 text-accent" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm mb-0.5">
            {title}
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            AI Generated Sample • {durationLabel}
          </p>

          <div className="flex items-center gap-3">
            <Button
              size="icon"
              variant="secondary"
              className="w-10 h-10 rounded-full flex-shrink-0"
              onClick={togglePlay}
            >
              {isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4 ml-0.5" />
              )}
            </Button>

            <div
              className="flex-1 h-2 bg-muted rounded-full cursor-pointer overflow-hidden"
              onClick={handleProgressClick}
            >
              <div
                className="h-full bg-accent rounded-full transition-all duration-100"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
        onError={() => setIsPlaying(false)}
        onPlay={() => onPlay?.()}
        src={src}
        preload="metadata"
      />
    </>
  );
}
